import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '@sbe-spinoff-schoolbell-e/web-shared';
// 백엔드 OAuthClientType(Prisma enum)을 계약 클라이언트 lib(`@sbe-spinoff-client/schoolbell-e-api`)에서 그대로 가져온다 —
// 값을 손으로 재선언하지 않는다(docs/development/web-openapi-orval.md). 재생성:
// `nx run client-schoolbell-e-api:generate`. schoolbell-e-web의 api.service.ts와
// 동일한 원칙 — App/OAuthClient 관련 메서드만 이 앱 몫으로 들고 온다(org/label/permission 등은
// schoolbell-e-web에 남아 있다, 2026-08-05 developer-web-domain-separation 플랜).
import { type OAuthClientType } from '@sbe-spinoff-client/schoolbell-e-api';
export type { OAuthClientType };

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private authHeaders(): HttpHeaders {
    const token = this.auth.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }

  private url(path: string): string {
    return `${environment.apiUrl}${path}`;
  }

  refresh() {
    return this.http.post<{ accessToken: string; userId: number; name: string | null }>(
      this.url('/auth/refresh'),
      {},
      { withCredentials: true },
    );
  }

  // schoolbell-e-web과 같은 sbe_refresh_token(HttpOnly, API 자체 도메인 scope) 쿠키를 공유한다 —
  // 두 앱이 물리적으로 분리돼 있어도 sessionStorage 세션은 origin별로 격리되지만, 이 refresh
  // 쿠키는 API 도메인(sbe-api(-staging).schoolbell-e.com) 기준이라 어느 앱에서 호출하든 로그인
  // 상태가 이어진다. 그래서 developer-web에는 별도 로그인 화면이 없다(auth.guard.ts 참고).
  async tryRestoreSessionFromCookie(): Promise<boolean> {
    try {
      const { accessToken, userId, name } = await firstValueFrom(this.refresh());
      this.auth.setSession({ accessToken, userId, name });
      return true;
    } catch (e) {
      // 세션 복원 실패는 로그인 페이지로 리디렉션되는 정상적인 흐름일 수 있으므로
      // 에러를 다시 던지지는 않지만, 디버깅을 위해 콘솔에 기록한다(Gemini 리뷰 반영).
      console.error('Failed to restore session from cookie:', e);
      return false;
    }
  }

  logout() {
    return this.http.post<{ ok: boolean }>(
      this.url('/auth/logout'),
      {},
      { withCredentials: true },
    );
  }

  getOAuthClientScopes() {
    return this.http.get<{ scopes: string[] }>(this.url('/developer/oauth-clients/scopes'), {
      headers: this.authHeaders(),
    });
  }

  getOAuthClient(id: string) {
    return this.http.get<OAuthClientResponse>(this.url(`/developer/oauth-clients/${id}`), {
      headers: this.authHeaders(),
    });
  }

  issueOAuthClientSecret(id: string) {
    return this.http.post<{ clientSecret: string }>(
      this.url(`/developer/oauth-clients/${id}/secret`),
      {},
      { headers: this.authHeaders() },
    );
  }

  // trustedEmbedOrigins 셀프서비스 편집(self-service 플랜 PR 3) — 배열 전체 교체.
  updateOAuthClientEmbedOrigins(id: string, origins: string[]) {
    return this.http.put<OAuthClientResponse>(
      this.url(`/developer/oauth-clients/${id}/embed-origins`),
      { origins },
      { headers: this.authHeaders() },
    );
  }

  // App(1):OAuthClient(N) 그룹화 — 2026-07-07-oauth-client-app-grouping.md.
  // 2026-07-08-oauth-app-level-approval.md 이후 심사 단위는 App이다 — App을 name+scope로
  // 신청해 승인받으면, 그 아래 client(createOAuthAppClient)는 심사 없이 즉시 생성된다.
  getOAuthApps() {
    return this.http.get<{ apps: OAuthClientAppSummary[] }>(this.url('/developer/apps'), {
      headers: this.authHeaders(),
    });
  }

  getOAuthApp(id: string) {
    return this.http.get<OAuthClientAppSummary>(this.url(`/developer/apps/${id}`), {
      headers: this.authHeaders(),
    });
  }

  createOAuthApp(body: OAuthAppInput) {
    return this.http.post<OAuthClientAppSummary>(this.url('/developer/apps'), body, {
      headers: this.authHeaders(),
    });
  }

  resubmitOAuthApp(id: string, body: OAuthAppInput) {
    return this.http.patch<OAuthClientAppSummary>(this.url(`/developer/apps/${id}`), body, {
      headers: this.authHeaders(),
    });
  }

  cancelOAuthApp(id: string) {
    return this.http.post<OAuthClientAppSummary>(
      this.url(`/developer/apps/${id}/cancel`),
      {},
      { headers: this.authHeaders() },
    );
  }

  // scope가 없다 — 부모 App이 승인받은 scope를 그대로 상속한다(client별 세분화는 후속 과제).
  createOAuthAppClient(appId: string, body: OAuthAppClientInput) {
    return this.http.post<OAuthClientResponse>(this.url(`/developer/apps/${appId}/oauth-clients`), body, {
      headers: this.authHeaders(),
    });
  }

  // 2026-08-17-oauth-app-scope-management-ui.md — Approved App에 scope 추가 신청. App
  // 자신의 status는 바뀌지 않고 별도 요청만 만들어져 staff 승인을 기다린다.
  createOAuthAppScopeRequest(appId: string, body: OAuthAppScopeRequestInput) {
    return this.http.post<OAuthClientAppScopeChangeRequestSummary>(
      this.url(`/developer/apps/${appId}/scope-requests`),
      body,
      { headers: this.authHeaders() },
    );
  }
}

// App(1):OAuthClient(N) 그룹화 아래 client 생성 전용 입력 — scope가 없다(부모 App의 승인된
// scope 상속, 2026-07-08-oauth-app-level-approval.md 이후 client 단위 신청은 존재하지 않는다).
export interface OAuthAppClientInput {
  name: string;
  clientType: OAuthClientType;
  redirectUri?: string;
  // 생성 시점에 바로 임베드 신뢰 origin을 등록할 수 있게 한다(사용자 지시 2026-08-22) —
  // 이전엔 client 생성 후 상세 화면에서 별도로 PUT(updateOAuthClientEmbedOrigins)해야만
  // 등록할 수 있었다.
  embedOrigins?: string[];
}

export interface OAuthAppInput {
  name: string;
  allowedScopes: string[];
}

export interface OAuthClientResponse {
  id: string;
  appId: string | null;
  clientId: string;
  name: string;
  clientType: OAuthClientType;
  redirectUri: string;
  scope: string[];
  createdAt: string;
  hasSecret: boolean;
  // popup/iframe(schoolbell-e 자체 호스팅) 및 제3자 RS 임베드 위젯이 신뢰할 origin
  // 화이트리스트 — self-service 플랜 PR 3.
  trustedEmbedOrigins: string[];
}

export interface OAuthClientAppSummary {
  id: string;
  name: string;
  allowedScopes: string[];
  status: 'Pending' | 'Approved' | 'Rejected' | 'Canceled';
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  clients: OAuthClientResponse[];
  // 최신순 — 2026-08-17-oauth-app-scope-management-ui.md.
  scopeChangeRequests: OAuthClientAppScopeChangeRequestSummary[];
}

export interface OAuthAppScopeRequestInput {
  requestedScopes: string[];
}

export interface OAuthClientAppScopeChangeRequestSummary {
  id: string;
  requestedScopes: string[];
  status: 'Pending' | 'Approved' | 'Rejected' | 'Canceled';
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
}
